import api from '../config/api';

class SMSVerificationService {
  /**
   * SMS doğrulama kodu gönder
   * @param {string} phoneNumber - Telefon numarası
   * @param {string} purpose - Doğrulama amacı
   * @returns {Promise<Object>} - API yanıtı
   */
  async sendVerificationCode(phoneNumber, purpose = 'cash_on_delivery') {
    try {
      console.log(`📱 SMS doğrulama kodu gönderiliyor: ${phoneNumber} (${purpose})`);
      
      const response = await api.post('/sms-verification/send', {
        phoneNumber: phoneNumber,
        purpose: purpose
      });

      console.log('✅ SMS gönderim yanıtı:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ SMS gönderim hatası:', error);
      
      if (error.response && error.response.data) {
        return {
          success: false,
          error: error.response.data.error || 'SMS gönderilemedi',
          waitSeconds: error.response.data.waitSeconds
        };
      }
      
      return {
        success: false,
        error: 'SMS gönderim sırasında hata oluştu'
      };
    }
  }

  /**
   * SMS doğrulama kodunu kontrol et
   * @param {string} phoneNumber - Telefon numarası
   * @param {string} verificationCode - Doğrulama kodu
   * @param {string} purpose - Doğrulama amacı
   * @returns {Promise<Object>} - API yanıtı
   */
  async verifyCode(phoneNumber, verificationCode, purpose = 'cash_on_delivery') {
    try {
      console.log(`🔍 SMS doğrulama kodu kontrol ediliyor: ${phoneNumber} (${purpose})`);
      
      const response = await api.post('/sms-verification/verify', {
        phoneNumber: phoneNumber,
        verificationCode: verificationCode,
        purpose: purpose
      });

      console.log('✅ SMS doğrulama yanıtı:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ SMS doğrulama hatası:', error);
      
      if (error.response && error.response.data) {
        return {
          success: false,
          error: error.response.data.error || 'Doğrulama başarısız',
          remainingAttempts: error.response.data.remainingAttempts
        };
      }
      
      return {
        success: false,
        error: 'Doğrulama sırasında hata oluştu'
      };
    }
  }

  /**
   * Telefon numarası doğrulanmış mı kontrol et
   * @param {string} phoneNumber - Telefon numarası
   * @param {string} purpose - Doğrulama amacı
   * @param {number} validityMinutes - Geçerlilik süresi (dakika)
   * @returns {Promise<Object>} - API yanıtı
   */
  async checkVerificationStatus(phoneNumber, purpose = 'cash_on_delivery', validityMinutes = 30) {
    try {
      console.log(`📋 SMS doğrulama durumu kontrol ediliyor: ${phoneNumber} (${purpose})`);
      
      const response = await api.get(`/sms-verification/status/${encodeURIComponent(phoneNumber)}`, {
        params: {
          purpose: purpose,
          validityMinutes: validityMinutes
        }
      });

      console.log('✅ SMS durum kontrol yanıtı:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ SMS durum kontrol hatası:', error);
      
      if (error.response && error.response.data) {
        return {
          success: false,
          verified: false,
          error: error.response.data.error || 'Durum kontrol edilemedi'
        };
      }
      
      return {
        success: false,
        verified: false,
        error: 'Durum kontrol sırasında hata oluştu'
      };
    }
  }

  /**
   * SMS doğrulama sistemi bilgilerini al
   * @returns {Promise<Object>} - Sistem bilgileri
   */
  async getSystemInfo() {
    try {
      console.log('ℹ️ SMS doğrulama sistem bilgileri alınıyor');
      
      const response = await api.get('/sms-verification/info');

      console.log('✅ SMS sistem bilgileri:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ SMS sistem bilgileri hatası:', error);
      
      return {
        success: false,
        error: 'Sistem bilgileri alınamadı',
        info: {
          codeLength: 6,
          expiryMinutes: 5,
          maxAttempts: 3,
          rateLimitMinutes: 1,
          validityMinutes: 30,
          purposes: ['cash_on_delivery', 'order_verification', 'account_verification']
        }
      };
    }
  }

  /**
   * Telefon numarasını formatla (frontend validation için)
   * @param {string} phoneNumber - Ham telefon numarası
   * @returns {string|null} - Formatlanmış telefon numarası
   */
  formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return null;
    
    // Sadece rakamları al
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Türkiye telefon numarası kontrolü
    if (cleanNumber.length === 11 && cleanNumber.startsWith('0')) {
      // 0 ile başlıyorsa format: 0555 123 45 67
      return cleanNumber.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
    } else if (cleanNumber.length === 10 && cleanNumber.startsWith('5')) {
      // 5 ile başlıyorsa format: 555 123 45 67
      return cleanNumber.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
    } else if (cleanNumber.length === 13 && cleanNumber.startsWith('90')) {
      // +90 ile başlıyorsa format: +90 555 123 45 67
      return cleanNumber.replace(/(\d{2})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 $2 $3 $4 $5');
    }
    
    return phoneNumber; // Tanınmayan format, olduğu gibi döndür
  }

  /**
   * Telefon numarası geçerli mi kontrol et
   * @param {string} phoneNumber - Telefon numarası
   * @returns {boolean} - Geçerli mi?
   */
  isValidPhoneNumber(phoneNumber) {
    if (!phoneNumber) return false;
    
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Türkiye telefon numarası formatları
    const validFormats = [
      /^0[5][0-9]{9}$/, // 05XXXXXXXXX
      /^[5][0-9]{9}$/,  // 5XXXXXXXXX
      /^90[5][0-9]{9}$/ // 905XXXXXXXXX
    ];
    
    return validFormats.some(format => format.test(cleanNumber));
  }

  /**
   * Kalan süreyi hesapla (doğrulama süresi için)
   * @param {string} expiresAt - ISO tarih string'i
   * @returns {number} - Kalan saniye
   */
  getRemainingTime(expiresAt) {
    if (!expiresAt) return 0;
    
    const expiryTime = new Date(expiresAt).getTime();
    const currentTime = new Date().getTime();
    const remaining = Math.max(0, Math.floor((expiryTime - currentTime) / 1000));
    
    return remaining;
  }

  /**
   * Süreyi formatla (MM:SS)
   * @param {number} seconds - Saniye
   * @returns {string} - Formatlanmış süre
   */
  formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}

// Singleton instance export et
const smsVerificationService = new SMSVerificationService();
export default smsVerificationService;
